import { AppContext } from "@/services/AppContext/appContext";
import { Button } from "@/shadcn/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/shadcn/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/shadcn/components/ui/popover";
import { ScrollArea } from "@/shadcn/components/ui/scroll-area";
import { Check, ChevronsUpDown, User } from "lucide-react";
import React, { useContext } from "react";

type MultiPickerProps = {
  assignees: number[];
  type: string;
  handleFieldAssignees: (id: number) => void;
  multiAssigneeRef: React.MutableRefObject<HTMLDivElement | null>;
  side?: "left" | "right" | "bottom" | "top" | undefined;
  align?: "start" | "center" | "end" | undefined;
  containerClassName?: string;
};

const MultiPicker: React.FC<MultiPickerProps> = ({
  assignees,
  type,
  handleFieldAssignees,
  multiAssigneeRef,
  side,
  align,
  containerClassName,
}) => {
  const { allAppUsers } = useContext(AppContext);

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  return (
    <div ref={multiAssigneeRef} className={containerClassName}>
      <Popover>
        <PopoverTrigger
          className="w-full"
          onMouseDown={(e) => {
            e.stopPropagation();
            e.preventDefault();
          }}
          onClick={(e) => {
            e.stopPropagation();
          }}
          asChild
        >
          <Button
            id="edit-assignees"
            variant="outline"
            role="combobox"
            className="justify-between w-full"
            onMouseDown={handleMouseDown}
          >
            {assignees && assignees.length > 0
              ? `${assignees.length} selected`
              : `Select ${type}`}
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          side={side ? side : "bottom"}
          align={align ? align : "end"}
          className="w-full p-0"
          ref={multiAssigneeRef}
          onMouseDown={(e) => {
            e.stopPropagation();
            e.preventDefault();
          }}
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          <Command>
            <CommandInput placeholder="Search assignees..." />
            <CommandList>
              <ScrollArea className="h-[200px]">
                <CommandEmpty>No assignees found.</CommandEmpty>
                <CommandGroup>
                  {allAppUsers.map((assignee) => (
                    <CommandItem
                      key={assignee.id}
                      onSelect={() => handleFieldAssignees(assignee.id)}
                      onMouseDown={handleMouseDown}
                    >
                      <Check
                        className={`mr-2 h-4 w-4 ${
                          assignees?.some((id) => id === assignee.id)
                            ? "opacity-100"
                            : "opacity-0"
                        }`}
                      />
                      <div className="flex items-center gap-1">
                        {assignee.profile_picture ? (
                          <img
                            className="size-4 rounded-full shrink-0"
                            src={assignee.profile_picture}
                          />
                        ) : (
                          <User className="size-4 rounded-full bg-primary shrink-0" />
                        )}
                        {assignee.first_name} {assignee.surname}
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </ScrollArea>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
};

export default MultiPicker;
